'use client'

import { motion } from 'motion/react'
import { ReactNode, useCallback, useRef, useState } from 'react'
import { ILabelStyleType } from '@/components/label'
import { LabeledContainer } from '@/components/labeled-container'
import { LoadingSpinner } from '@/components/loading-spinner'
import { SvgOutlinedSearch } from '@/icons/svg-outlined-search'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IInputProps {
    inputClassName?: string
    containerClassName?: string
    value: string
    onValueChange: (value: string) => void
    styleType?: 'grey' | 'white'
    type?: 'search' | 'default'
    shape?: 'pill' | 'rounded'
    placeholder?: string
    label?: ReactNode
    labeledContainerClassName?: string
    labelStyleType?: ILabelStyleType
    onFocus?: () => void
    onBlur?: () => void
    onClick?: () => void
    isDisabled?: boolean
    isLoading?: boolean
    autoFocus?: boolean
    name?: string
    htmlType?: 'text' | 'number' | 'email' | 'password' | 'date'
    rows?: number
    autoComplete?: string
    motionBorderColor?: string
}

export const Input = ({
    inputClassName,
    containerClassName,
    value,
    name,
    onValueChange,
    styleType = 'grey',
    type = 'default',
    shape = 'rounded',
    placeholder,
    label,
    labeledContainerClassName,
    labelStyleType,
    onClick,
    onFocus: propsOnFocus,
    onBlur: propsOnBlur,
    isLoading,
    isDisabled,
    autoFocus,
    autoComplete,
    htmlType = 'text',
    rows,
    motionBorderColor = COLORS.black,
}: IInputProps) => {
    const [isFocused, setIsFocused] = useState(false)

    const onFocus = useCallback(() => {
        setIsFocused(true)
        propsOnFocus?.()
    }, [propsOnFocus])

    const onBlur = useCallback(() => {
        propsOnBlur?.()
        setIsFocused(false)
    }, [propsOnBlur])

    /**
     * Helper function to handle input value change
     */
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => {
            onValueChange(e.target.value)
        },
        [onValueChange]
    )

    const backgroundColor = styleType === 'grey' ? '#F3F3F3' : '#fff'
    const focusBackgroundColor = styleType === 'grey' ? '#ebebeb' : '#fff'

    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)

    const isTextarea = Boolean(rows && rows > 1)

    const borderRadius = shape === 'pill' ? '999px' : '12px'

    const props = {
        ref: inputRef,
        type: htmlType,
        disabled: isDisabled,
        autoComplete,
        name,
        autoFocus,
        onClick,
        initial: {
            backgroundColor,
            borderColor: styleType !== 'grey' ? motionBorderColor : undefined,
        },
        animate: {
            backgroundColor: isFocused ? focusBackgroundColor : backgroundColor,
            borderColor:
                styleType !== 'grey' ? (isFocused ? COLORS.black : motionBorderColor) : undefined,
        },
        whileHover: { borderColor: styleType !== 'grey' ? COLORS.black : undefined },
        transition: { transition: { duration: 0.18, ease: 'easeOut' } },
        onFocus,
        onBlur,
        className: cn(
            'min-h-[48px] flex flex-row placeholder:text-[#828282] focus:outline-hidden w-full',
            styleType !== 'grey' && 'border-[1px] border-solid',
            !isTextarea && 'h-[48px]',
            inputClassName
        ),
        style: {
            borderRadius,
            backgroundColor: styleType === 'grey' ? '#F3F3F3' : '#fff',
            padding: type === 'search' ? '0 16px 0 54px' : isTextarea ? '12px 16px' : '0 16px',
        },
        value,
        onChange,
        placeholder,
        rows,
    }

    const rawInputNode = isTextarea ? (
        <motion.textarea {...props} rows={rows} />
    ) : (
        <motion.input {...props} />
    )

    const inputNode = (
        <motion.div className={cn('relative', containerClassName)}>
            {type === 'search' && (
                <div className="transform-center-y left-5">
                    <SvgOutlinedSearch color={COLORS.black} size={24} />
                </div>
            )}

            {rawInputNode}

            {inputRef.current && styleType !== 'grey' && (
                <motion.div
                    initial={{ opacity: isFocused ? 1 : 0 }}
                    animate={{ opacity: isFocused ? 1 : 0 }}
                    className="absolute left-[50%] top-[50%] z-10 border-neotaste/30 border-solid border-[7px] z-[-1]
                        transform-center"
                    transition={{ duration: 0.2 }}
                    style={{
                        borderRadius,
                        height: inputRef.current.scrollHeight + 10,
                        width: inputRef.current.scrollWidth + 10,
                    }}
                />
            )}

            {isLoading && (
                <div className="transform-center-y right-5">
                    <LoadingSpinner size={24} color="black" />
                </div>
            )}
        </motion.div>
    )

    switch (Boolean(label)) {
        case true:
            return (
                <LabeledContainer
                    label={label}
                    labelStyleType={labelStyleType}
                    className={cn(labeledContainerClassName)}
                >
                    {inputNode}
                </LabeledContainer>
            )

        case false:
            return inputNode
    }
}
